import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const skeletonVariants = cva(
  // A quiet, theme-aware placeholder fill that gently pulses. The fill is a
  // low-opacity wash of the primary text color over the card surface, so it
  // stays visible on both the light (white) and dark (slate) admin surfaces
  // without a dedicated token. `motion-reduce:animate-none` honors the user's
  // reduced-motion preference purely in CSS — no per-instance JS media query.
  "block shrink-0 animate-pulse bg-[color-mix(in_srgb,var(--color-text-primary),var(--color-surface-primary)_88%)] motion-reduce:animate-none",
  {
    variants: {
      shape: {
        /** A single line of text; pair with a `w-*`/`h-*` override as needed. */
        text: "h-4 w-full rounded-sm",
        /** A round avatar / logo / icon; size it with `size-*`. */
        circle: "aspect-square rounded-full",
        /** A bare rounded block — thumbnails, tiles, buttons; size it yourself. */
        rect: "rounded-sm",
      },
    },
    defaultVariants: {
      shape: "text",
    },
  },
);

type AdminSkeletonProps = React.ComponentProps<"span"> & VariantProps<typeof skeletonVariants>;

/**
 * Base skeleton placeholder — a pulsing block that reserves layout space while
 * content loads. Size it with `className` (e.g. `h-8 w-40`), or reach for a
 * shape wrapper ({@link AdminSkeletonText}, {@link AdminSkeletonCircle}) / the
 * table skeleton (`AdminFlatTableSkeleton`).
 *
 * Skeletons are decorative and marked `aria-hidden`; announce the loading state
 * once on the surrounding region (e.g. `aria-busy` + an sr-only status), not on
 * each block.
 */
const AdminSkeleton = ({ className, shape, ...props }: AdminSkeletonProps) => (
  <span data-slot="skeleton" aria-hidden className={cn(skeletonVariants({ shape }), className)} {...props} />
);

type AdminSkeletonTextProps = Omit<React.ComponentProps<"div">, "children"> & {
  /** Number of placeholder lines. Default `3`. */
  lines?: number;
  /** Width utility for the last line, giving the paragraph a ragged edge. Default `w-3/5`. */
  lastLineWidthClassName?: string;
  /** Height utility applied to every line. Default `h-3.5`. */
  lineClassName?: string;
};

/**
 * Multi-line text placeholder — a "paragraph" of skeleton lines. When more than
 * one line is requested the final line is shortened so the block reads as prose
 * rather than a solid rectangle.
 */
const AdminSkeletonText = ({
  lines = 3,
  lastLineWidthClassName = "w-3/5",
  lineClassName = "h-3.5",
  className,
  ...props
}: AdminSkeletonTextProps) => (
  <div className={cn("flex w-full flex-col gap-2", className)} {...props}>
    {Array.from({ length: Math.max(1, lines) }, (_, index) => (
      <AdminSkeleton
        key={index}
        shape="text"
        className={cn(lineClassName, lines > 1 && index === lines - 1 && lastLineWidthClassName)}
      />
    ))}
  </div>
);

type AdminSkeletonCircleProps = Omit<AdminSkeletonProps, "shape"> & {
  /** Diameter utility (width + height), e.g. `size-12`. Default `size-10`. */
  sizeClassName?: string;
};

/** Circular placeholder for avatars, logos, and round icons. */
const AdminSkeletonCircle = ({ sizeClassName = "size-10", className, ...props }: AdminSkeletonCircleProps) => (
  <AdminSkeleton shape="circle" className={cn(sizeClassName, className)} {...props} />
);

type AdminSkeletonRegionProps = {
  /**
   * Announced once on a visually-hidden `role="status"` live region (e.g.
   * "Loading campaigns..."). The `status` role doesn't take its name from
   * content, so a string label also becomes the region's `aria-label`; a
   * non-string label (e.g. localized JSX) is still rendered as the announced
   * live-region content.
   */
  label: React.ReactNode;
  /** The decorative skeleton subtree — hidden from assistive tech. */
  children: React.ReactNode;
};

/**
 * Accessible wrapper for a skeleton loading state: a single visually-hidden
 * `role="status"` region that announces {@link label}, plus the skeleton subtree
 * hidden from assistive tech via `aria-hidden` on a `display: contents` wrapper
 * (so the page's flex/grid rhythm still reaches the skeleton's sections). Use it
 * wherever a spinner — which exposed `role="status"` — is replaced by a bare
 * skeleton outside a gate, e.g. campaign pages whose conditionally-`enabled`
 * query can't be handed to a gate. `AdminQueryGate` renders this same wrapper
 * around its `loadingFallback`.
 */
const AdminSkeletonRegion = ({ label, children }: AdminSkeletonRegionProps) => (
  <>
    <span role="status" aria-label={typeof label === "string" ? label : undefined} className="sr-only">
      {label}
    </span>
    <div aria-hidden className="contents">
      {children}
    </div>
  </>
);

export { AdminSkeleton, AdminSkeletonText, AdminSkeletonCircle, AdminSkeletonRegion };
